import { CheckCircle2 } from "lucide-react";
import { SSEConnectionStatus } from "../feedback/SSEConnectionStatus";
import type { SSEConnectionState } from "../../hooks/useSSEStream";

interface StatusBarProps {
  /**
   * Current SSE connection state. Passing `undefined` (default) means
   * "there is no active SSE stream" and the indicator is hidden. The
   * previous default of `'disconnected'` caused a misleading
   * "Reconnecting…" footer to show on screens where profiling hadn't
   * even started yet.
   */
  sseState?: SSEConnectionState;
  pollingIntervalSec?: number;
  onSSEReconnect?: () => void;
  checkpointStatus?: string;
}

export function StatusBar({
  sseState,
  pollingIntervalSec,
  onSSEReconnect,
  checkpointStatus,
}: StatusBarProps) {
  // Only show the SSE indicator when profiling is actually in flight.
  // 'disconnected' in particular is the default pre-stream state and
  // shouldn't surface a "Reconnecting…" message.
  const showSSE =
    sseState === "connected" ||
    sseState === "slow" ||
    sseState === "polling" ||
    sseState === "stopped";

  return (
    <footer className="h-8 flex items-center justify-between px-4 text-caption border-t border-[var(--border)] glass-surface">
      {/* Left: SSE indicator (only while a stream is active) */}
      <div>
        {showSSE && sseState && (
          <SSEConnectionStatus
            state={sseState}
            pollingIntervalSec={pollingIntervalSec}
            onReconnect={onSSEReconnect}
          />
        )}
      </div>

      {/* Right: Checkpoint status */}
      <div className="flex items-center gap-2 text-[var(--muted-foreground)]">
        {checkpointStatus && (
          <>
            <CheckCircle2 className="w-3 h-3" />
            <span>{checkpointStatus}</span>
          </>
        )}
      </div>
    </footer>
  );
}
